/**
 * @file InviteLinkPanel.tsx
 * @description Ссылка приглашения в ЛК после отправки инвайта (замечание
 * пользователя 2026-09-22): письмо могло не дойти или клиент попросил в
 * мессенджер — менеджер копирует ссылку и отправляет руками.
 *
 * OQ-70 (2026-09-24): сервер хранит токен зашифрованным и отдаёт ссылку
 * действующего инвайта в статусе ЛК — блок показывает её всегда, пока инвайт
 * не истёк; «Переотправить инвайт» выпускает новую, старая гаснет.
 */

import { Copy } from "lucide-react";

import { Button } from "../../../../components/ui/button";
import { useNotify } from "../../../../components/ui/NotifyProvider";

interface Props {
  url: string;
  /** До какого момента ссылка действует (ISO). */
  expiresAt: string;
  formatDateTime: (iso: string) => string;
}

export function InviteLinkPanel({ url, expiresAt, formatDateTime }: Props) {
  const notify = useNotify();

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      notify.success("Ссылка скопирована — можно отправить клиенту");
    } catch {
      notify.warning("Скопировать не вышло — выделите ссылку ниже вручную");
    }
  }

  return (
    <section
      aria-label="Ссылка приглашения"
      className="mt-4 flex flex-col gap-2 rounded-xl bg-surface-low px-4 py-3 text-sm"
    >
      <div className="font-medium">Ссылка приглашения — можно отправить клиенту вручную</div>
      <code className="block select-all break-all rounded-md bg-surface-lowest px-3 py-2 text-xs">
        {url}
      </code>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="min-h-11" onClick={() => void copy()}>
          <Copy className="mr-1 size-4" />
          Скопировать ссылку
        </Button>
        <span className="text-xs text-on-surface-variant">
          действует до {formatDateTime(expiresAt)} · та же ссылка, что в письме;
          «Переотправить инвайт» выпустит новую, старая перестанет работать
        </span>
      </div>
    </section>
  );
}
